/**
 * Presenting hubs in a picker: schema-qualified, and in an order a human can scan.
 *
 * Hubs arrive in whatever order the API returned them, which is creation order — Max's report was
 * that finding a hub among many is hard because the list is not alphabetical. Two hubs can also
 * share a table name across schemas, so the name alone does not identify one.
 */

export type HubOptionSource = {
  id: string;
  record?: {
    target_table?: string | null;
    target_schema?: string | null;
  } | null;
};

export type HubOption = {
  id: string;
  /** What the option reads as: `rdv.customer_h`, or just the table when there is no schema. */
  label: string;
  targetTable: string;
  targetSchema: string;
};

const text = (value: string | null | undefined) => (value ?? "").trim();

/** `rdv` + `customer_h` -> `rdv.customer_h`. Falls back to whatever is known. */
export const hubOptionLabel = (
  targetSchema: string | null | undefined,
  targetTable: string | null | undefined,
  fallback = ""
): string => {
  const schema = text(targetSchema);
  const table = text(targetTable) || text(fallback);
  if (!table) {
    return schema;
  }
  return schema ? `${schema}.${table}` : table;
};

/**
 * Sort by schema, then table name.
 *
 * `numeric` so `dv_entity_2` follows `dv_entity_1` rather than `dv_entity_10`, and `sensitivity:
 * "base"` so case and accents do not split otherwise-adjacent names apart.
 */
export const buildHubOptions = (hubs: HubOptionSource[]): HubOption[] =>
  hubs
    .map((hub) => ({
      id: hub.id,
      targetTable: text(hub.record?.target_table) || hub.id,
      targetSchema: text(hub.record?.target_schema),
      label: hubOptionLabel(hub.record?.target_schema, hub.record?.target_table, hub.id)
    }))
    .sort((left, right) => {
      const bySchema = left.targetSchema.localeCompare(right.targetSchema, undefined, {
        numeric: true,
        sensitivity: "base"
      });
      if (bySchema !== 0) {
        return bySchema;
      }
      return left.targetTable.localeCompare(right.targetTable, undefined, {
        numeric: true,
        sensitivity: "base"
      });
    });

/**
 * Compare two names the way a person scanning a list expects.
 *
 * `numeric` so `entity_2` precedes `entity_10`; `sensitivity: "base"` so case and accents do not
 * fling `Ärende` to the end of a Swedish list or split `Beta` from `alpha` and `gamma`. Shared so
 * every list in the app orders the same way — the data catalog listed sources and tables in
 * whatever order the API returned them, which is creation order.
 */
export const compareNames = (left: string | null | undefined, right: string | null | undefined) =>
  (left ?? "").localeCompare(right ?? "", undefined, { numeric: true, sensitivity: "base" });
